import React, { useState, useMemo } from "react";
import toast from "react-hot-toast";
import { formatDistanceToNow } from "date-fns";
import api from "../../../../chat/api";
import EditorPic from "./Editor.png";
import RemoveMemberPopover from "./RemoveMemberPopover";
import {
  ExpandCollapseIcon,
  ApproveIcon,
  ChangesIcon,
  PendingIcon,
  ReviewIcon,
} from "./svgIcons";

const ButtonCheckIcon = ({ className }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    className={className}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18ZM13.7071 8.70711C14.0976 8.31658 14.0976 7.68342 13.7071 7.29289C13.3166 6.90237 12.6834 6.90237 12.2929 7.29289L9 10.5858L7.70711 9.29289C7.31658 8.90237 6.68342 8.90237 6.29289 9.29289C5.90237 9.68342 5.90237 10.3166 6.29289 10.7071L8.29289 12.7071C8.68342 13.0976 9.31658 13.0976 9.70711 12.7071L13.7071 8.70711Z"
      fill="currentColor"
    />
  </svg>
);

const ResetIcon = ({ className }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    className={className}
  >
    <path
      d="M13.6 8C13.6 11.0928 11.0928 13.6 8 13.6C4.90721 13.6 2.4 11.0928 2.4 8C2.4 4.90721 4.90721 2.4 8 2.4C9.50868 2.4 10.875 2.99616 11.8841 3.96154L13.6 5.6M13.6 5.6V2.4M13.6 5.6H10.4"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const RedCrossIcon = ({ className }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    className={className}
  >
    <circle cx="10" cy="10" r="10" fill="#DC2626" />
    <path
      d="M13.5355 6.46447C13.7308 6.65973 13.7308 6.97631 13.5355 7.17157L10.7071 10L13.5355 12.8284C13.7308 13.0237 13.7308 13.3403 13.5355 13.5355C13.3403 13.7308 13.0237 13.7308 12.8284 13.5355L10 10.7071L7.17157 13.5355C6.97631 13.7308 6.65973 13.7308 6.46447 13.5355C6.2692 13.3403 6.2692 13.0237 6.46447 12.8284L9.29289 10L6.46447 7.17157C6.2692 6.97631 6.2692 6.65973 6.46447 6.46447C6.65973 6.2692 6.97631 6.2692 7.17157 6.46447L10 9.29289L12.8284 6.46447C13.0237 6.2692 13.3403 6.2692 13.5355 6.46447Z"
      fill="white"
    />
  </svg>
);

const ReviewStatusTab = ({
  activeRightSection,
  filterType,
  displayedMembers,
  expandedMemberIds,
  toggleMemberCard,
  getInitials,
  orgId,
  folderId,
  onRefresh,
  userPermission,
  currentUserEmail,
}) => {
  const [reminderStatuses, setReminderStatuses] = useState({});
  const [markDoneStatuses, setMarkDoneStatuses] = useState({});
  const [resetStatuses, setResetStatuses] = useState({});
  const [reviewerActionStatuses, setReviewerActionStatuses] = useState({});

  // --- Request Changes State ---
  const [requestingChangesUserId, setRequestingChangesUserId] = useState(null);
  const [changeRequestText, setChangeRequestText] = useState("");

  // --- Filtering Logic ---
  const filteredMembers = useMemo(() => {
    if (
      userPermission === "admin" ||
      userPermission === "owner" ||
      !userPermission
    ) {
      return displayedMembers;
    }
    return displayedMembers.filter(
      (member) => member.email === currentUserEmail
    );
  }, [displayedMembers, userPermission, currentUserEmail]);

  const getSectionId = () => {
    return activeRightSection.id || activeRightSection.section_id;
  };

  // --- Handlers ---
  const handleSendReminder = async (memberUserId) => {
    if (!orgId || !folderId || !activeRightSection) {
      toast.error("Missing required project information");
      return;
    }
    setReminderStatuses((prev) => ({ ...prev, [memberUserId]: "sending" }));

    try {
      const payload = {
        org_id: parseInt(orgId),
        section_id: getSectionId(),
        user_id: memberUserId,
      };
      await api.post(
        `/api/v1/rfp/folders/${folderId}/section-view/send-reminder/`,
        payload
      );
      setReminderStatuses((prev) => ({ ...prev, [memberUserId]: "success" }));
      toast.success("Reminder sent successfully");
      setTimeout(() => {
        setReminderStatuses((prev) => ({
          ...prev,
          [memberUserId]: "disabled",
        }));
      }, 2000);
    } catch (error) {
      console.error("Error sending reminder:", error);
      toast.error(error?.response?.data?.error || "Failed to send reminder");
      setReminderStatuses((prev) => {
        const newState = { ...prev };
        delete newState[memberUserId];
        return newState;
      });
    }
  };

  const handleMarkAsDone = async (memberUserId) => {
    if (!orgId || !folderId || !activeRightSection) return;
    setMarkDoneStatuses((prev) => ({ ...prev, [memberUserId]: "loading" }));

    try {
      const payload = {
        org_id: parseInt(orgId),
        section_id: getSectionId(),
        done: true,
      };
      await api.post(
        `/api/v1/rfp/folders/${folderId}/section-view/editor-mark-done/`,
        payload
      );
      setMarkDoneStatuses((prev) => ({ ...prev, [memberUserId]: "success" }));
      toast.success("Section marked as done");
      setTimeout(() => {
        setMarkDoneStatuses((prev) => {
          const newState = { ...prev };
          delete newState[memberUserId];
          return newState;
        });
        if (onRefresh) onRefresh();
      }, 2000);
    } catch (error) {
      console.error("Error marking as done:", error);
      toast.error("Failed to update status");
      setMarkDoneStatuses((prev) => {
        const newState = { ...prev };
        delete newState[memberUserId];
        return newState;
      });
    }
  };

  const handleResetStatus = async (memberUserId) => {
    if (!orgId || !folderId || !activeRightSection) return;
    setResetStatuses((prev) => ({ ...prev, [memberUserId]: true }));

    try {
      const payload = {
        org_id: parseInt(orgId),
        section_id: getSectionId(),
        user_id: memberUserId,
      };
      await api.post(
        `/api/v1/rfp/folders/${folderId}/section-view/reset-status/`,
        payload
      );
      toast.success("Review status reset");
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Error resetting status:", error);
      toast.error("Failed to reset status");
    } finally {
      setResetStatuses((prev) => {
        const newState = { ...prev };
        delete newState[memberUserId];
        return newState;
      });
    }
  };

  const handleRemoveMember = async (memberUserId) => {
    if (!orgId || !folderId || !activeRightSection)
      throw new Error("Missing info");
    const payload = {
      org_id: parseInt(orgId),
      section_id: getSectionId(),
      user_id: memberUserId,
    };
    await api.post(
      `/api/v1/rfp/folders/${folderId}/section-view/unassign-member/`,
      payload
    );
  };

  const handleApprove = async (memberUserId) => {
    if (!orgId || !folderId || !activeRightSection) return;
    setReviewerActionStatuses((prev) => ({
      ...prev,
      [memberUserId]: "loading-approve",
    }));

    try {
      const payload = {
        org_id: parseInt(orgId),
        section_id: getSectionId(),
        approved: true,
      };
      await api.post(
        `/api/v1/rfp/folders/${folderId}/section-view/reviewer-approve/`,
        payload
      );
      setReviewerActionStatuses((prev) => ({
        ...prev,
        [memberUserId]: "success-approve",
      }));
      toast.success("Section approved");
      setTimeout(() => {
        setReviewerActionStatuses((prev) => {
          const newState = { ...prev };
          delete newState[memberUserId];
          return newState;
        });
        if (onRefresh) onRefresh();
      }, 2000);
    } catch (error) {
      console.error("Error approving section:", error);
      toast.error("Failed to approve section");
      setReviewerActionStatuses((prev) => {
        const newState = { ...prev };
        delete newState[memberUserId];
        return newState;
      });
    }
  };

  const handleStartRequestChanges = (memberUserId) => {
    setRequestingChangesUserId(memberUserId);
    setChangeRequestText("");
  };

  const handleCancelRequestChanges = () => {
    setRequestingChangesUserId(null);
    setChangeRequestText("");
  };

  const handleSubmitRequestChanges = async (memberUserId) => {
    if (!changeRequestText.trim()) {
      toast.error("Please enter a reason for changes");
      return;
    }

    setReviewerActionStatuses((prev) => ({
      ...prev,
      [memberUserId]: "loading-changes",
    }));

    try {
      const payload = {
        org_id: parseInt(orgId),
        section_id: getSectionId(),
        change_request: changeRequestText,
      };

      await api.post(
        `/api/v1/rfp/folders/${folderId}/section-view/reviewer-request-change/`,
        payload
      );

      setReviewerActionStatuses((prev) => ({
        ...prev,
        [memberUserId]: "success-changes",
      }));
      toast.success("Change request saved");

      setTimeout(() => {
        setRequestingChangesUserId(null);
        setChangeRequestText("");
        setReviewerActionStatuses((prev) => {
          const newState = { ...prev };
          delete newState[memberUserId];
          return newState;
        });
        if (onRefresh) onRefresh();
      }, 1500);
    } catch (error) {
      console.error("Error requesting changes:", error);
      toast.error("Failed to save change request");
      setReviewerActionStatuses((prev) => {
        const newState = { ...prev };
        delete newState[memberUserId];
        return newState;
      });
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "done":
      case "approved":
      case "completed":
        return <ApproveIcon />;
      case "changes_requested":
        return <RedCrossIcon />; // Red Cross
      case "pending":
      default:
        return <PendingIcon />;
    }
  };

  return (
    <div className="flex-1 overflow-y-auto p-6 pt-0 custom-scrollbar">
      {filteredMembers.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="mb-6 relative">
            <img
              src={EditorPic}
              alt="No team members"
              className="w-48 h-auto object-contain"
            />
          </div>
          <p className="text-[#6B7280] font-outfit text-base font-medium text-center mb-1">
            {activeRightSection?.assigned_members?.length > 0
              ? `No members found with role "${filterType}"`
              : "No team members assigned yet!"}
          </p>
        </div>
      )}

      {filteredMembers.length > 0 && (
        <div className="space-y-4">
          {filteredMembers.map((member, idx) => {
            const isExpanded = expandedMemberIds.has(member.user_id);
            const rawStatus = member.review_status?.status || "pending";
            const reviewStatus = rawStatus.toLowerCase();
            const isDone =
              reviewStatus === "done" ||
              reviewStatus === "completed" ||
              reviewStatus === "approved";
            const isPending = reviewStatus === "pending";
            const isChangesRequested = reviewStatus === "changes_requested";

            // State Flags
            const reminderStatus = reminderStatuses[member.user_id];
            const isSendingReminder = reminderStatus === "sending";
            const isReminderSuccess = reminderStatus === "success";
            const isReminderDisabled = reminderStatus === "disabled";

            const doneStatus = markDoneStatuses[member.user_id];
            const isMarking = doneStatus === "loading";
            const isMarkedSuccess = doneStatus === "success";

            const isResetting = resetStatuses[member.user_id];

            const reviewerAction = reviewerActionStatuses[member.user_id];
            const isApproving = reviewerAction === "loading-approve";
            const isApprovedSuccess = reviewerAction === "success-approve";
            const isSavingChanges = reviewerAction === "loading-changes";
            const isSavedChangesSuccess = reviewerAction === "success-changes";

            // UI Mode Flags
            const isRequestingChangesMode =
              requestingChangesUserId === member.user_id;

            // Permissions
            const isReviewer = userPermission === "reviewer";
            const isEditor = userPermission === "editor";
            const isAdminOrOwner =
              userPermission === "admin" || userPermission === "owner";

            const showEditorMarkDone = isEditor && !isDone;

            // Reviewer: Hide if changes requested or already done
            const showReviewerActions =
              isReviewer &&
              !isDone &&
              !isChangesRequested &&
              !isRequestingChangesMode;

            const showAdminControls =
              isAdminOrOwner || (!isReviewer && !isEditor);

            // Get latest change request if exists
            const latestChangeRequest =
              member.review_status?.change_requests &&
              member.review_status.change_requests.length > 0
                ? member.review_status.change_requests[
                    member.review_status.change_requests.length - 1
                  ]
                : null;

            return (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg bg-white shadow-sm overflow-hidden"
              >
                <div
                  className="p-4 flex items-center gap-3 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleMemberCard(member.user_id)}
                >
                  <div className="w-10 h-10 rounded-full bg-[#C95EBE] flex items-center justify-center text-white font-outfit font-medium flex-shrink-0">
                    {getInitials(member.full_name || member.email)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="text-[#18101C] font-outfit text-[14px] font-medium truncate">
                        {member.full_name || member.email}
                      </p>
                      <span className="bg-[#FFF2FE] text-[#C95EBE] text-[10px] px-2 py-0.5 rounded-[8px] font-medium">
                        {member.role || "Editor"}
                      </span>
                    </div>
                    <p className="text-[#3F3F46] font-outfit text-[12px]">
                      {member.review_status?.sections_assigned || 1} Section
                      {(member.review_status?.sections_assigned || 1) > 1
                        ? "s"
                        : ""}{" "}
                      Assigned
                    </p>
                  </div>

                  <ExpandCollapseIcon
                    className={`transform transition-transform duration-200 ${
                      isExpanded ? "rotate-180" : ""
                    }`}
                  />
                </div>

                {isExpanded && (
                  <div className="px-4 pb-4 pt-0">
                    <div className="h-px w-full bg-gray-100 mb-4"></div>

                    <div className="mb-4">
                      <label className="text-[#3F3F46] font-outfit text-[14px] font-medium mb-2 block">
                        Sections Assigned (
                        {member.review_status?.sections_assigned || 1})
                      </label>

                      <div className="space-y-3">
                        <div className="bg-[#F9FAFB] border border-gray-200 rounded-lg p-3">
                          <div className="flex items-start justify-between mb-2">
                            <p className="text-[#374151] font-outfit text-[14px] font-medium flex-1">
                              {activeRightSection.number}.{" "}
                              {activeRightSection.title}
                            </p>
                            <div className="flex-shrink-0 ml-2">
                              {getStatusIcon(reviewStatus)}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Display "Changes Requested" Details Box */}
                      {isChangesRequested && latestChangeRequest && (
                        <div className="mt-3 bg-white border border-[#EAB308] rounded-lg p-4">
                          <h4 className="text-[#EAB308] font-outfit text-[14px] font-semibold mb-2">
                            Changes Requested
                          </h4>
                          <p className="text-[#374151] font-outfit text-[14px] leading-relaxed mb-3">
                            {latestChangeRequest.comment}
                          </p>
                          {latestChangeRequest.created_at && (
                            <p className="text-[#6B7280] font-outfit text-xs">
                              {formatDistanceToNow(
                                new Date(latestChangeRequest.created_at),
                                { addSuffix: true }
                              )}
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* --- Expanded "Reason for Changes" Form (Request Mode) --- */}
                    {isRequestingChangesMode && (
                      <div className="mt-2 mb-2 animate-in fade-in slide-in-from-top-2 duration-200">
                        <label className="block text-[#3F3F46] font-outfit text-[14px] font-medium mb-2">
                          Reason for Changes
                        </label>
                        <textarea
                          value={changeRequestText}
                          onChange={(e) => setChangeRequestText(e.target.value)}
                          disabled={isSavingChanges || isSavedChangesSuccess}
                          className="w-full border border-gray-300 rounded-lg p-3 text-[14px] font-outfit focus:outline-none focus:ring-1 focus:ring-[#C95EBE] focus:border-[#C95EBE] resize-none mb-3 disabled:bg-gray-50 disabled:text-gray-500"
                          rows="4"
                          placeholder="Describe what changes are needed..."
                        />
                        <div className="flex items-center justify-end gap-3">
                          <button
                            onClick={handleCancelRequestChanges}
                            disabled={isSavingChanges || isSavedChangesSuccess}
                            className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-[#374151] text-[14px] font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() =>
                              handleSubmitRequestChanges(member.user_id)
                            }
                            disabled={isSavingChanges || isSavedChangesSuccess}
                            className="px-6 py-2 bg-[#C95EBE] rounded-lg text-white text-[14px] font-medium hover:bg-[#B845AD] transition-colors disabled:opacity-70 min-w-[100px]"
                          >
                            {isSavingChanges
                              ? "Saving..."
                              : isSavedChangesSuccess
                              ? "Saved"
                              : "Submit"}
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center gap-2">
                      {/* Editor View */}
                      {showEditorMarkDone && (
                        <button
                          onClick={() => handleMarkAsDone(member.user_id)}
                          disabled={isMarking || isMarkedSuccess}
                          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-[14px] font-medium transition-all bg-[#22C55E] hover:bg-[#16A34A] text-white disabled:opacity-70 disabled:cursor-not-allowed shadow-sm"
                        >
                          {isMarking ? (
                            "Hang on..."
                          ) : isMarkedSuccess ? (
                            "Done"
                          ) : (
                            <>
                              <ButtonCheckIcon className="w-5 h-5" />
                              Mark as Done
                            </>
                          )}
                        </button>
                      )}

                      {/* Reviewer View */}
                      {showReviewerActions && (
                        <div className="flex items-center gap-3 w-full">
                          <button
                            onClick={() =>
                              handleStartRequestChanges(member.user_id)
                            }
                            disabled={isApproving || isApprovedSuccess}
                            className="flex-1 px-4 py-2.5 rounded-lg text-[14px] font-medium border border-[#E11D48] text-[#E11D48] bg-white hover:bg-[#FFF1F2] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Request Changes
                          </button>

                          <button
                            onClick={() => handleApprove(member.user_id)}
                            disabled={isApproving || isApprovedSuccess}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-[14px] font-medium bg-[#22C55E] hover:bg-[#16A34A] text-white transition-colors shadow-sm disabled:opacity-70 disabled:cursor-not-allowed"
                          >
                            {isApproving ? (
                              "Approving..."
                            ) : isApprovedSuccess ? (
                              "Approved"
                            ) : (
                              <>
                                <ButtonCheckIcon className="w-5 h-5" />
                                Approve
                              </>
                            )}
                          </button>
                        </div>
                      )}

                      {/* Admin / Owner View */}
                      {showAdminControls && (
                        <>
                          <RemoveMemberPopover
                            onConfirm={() =>
                              handleRemoveMember(member.user_id)
                            }
                            onAfterSuccess={onRefresh}
                            buttonClassName="flex-1 w-full px-4 py-2 bg-white border border-gray-200 rounded-lg text-[#374151] text-[14px] font-medium hover:bg-gray-50 transition-colors"
                          />

                          {/* Show Reset Status if DONE or CHANGES_REQUESTED */}
                          {isDone || isChangesRequested ? (
                            <button
                              onClick={() => handleResetStatus(member.user_id)}
                              disabled={isResetting}
                              className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-200 bg-white rounded-lg text-[14px] font-medium text-[#6B7280] hover:bg-gray-50 transition-colors flex-1"
                            >
                              <ResetIcon
                                className={`w-4 h-4 ${
                                  isResetting ? "animate-spin" : ""
                                }`}
                              />
                              {isResetting ? "Resetting..." : "Reset Status"}
                            </button>
                          ) : isPending ? (
                            <button
                              onClick={() => handleSendReminder(member.user_id)}
                              disabled={
                                isSendingReminder ||
                                isReminderSuccess ||
                                isReminderDisabled
                              }
                              className={`flex items-center justify-center gap-2 px-4 py-2 border rounded-lg text-[14px] font-medium transition-colors flex-1 ${
                                isReminderSuccess
                                  ? "bg-green-50 border-green-200 text-green-700 cursor-default"
                                  : isSendingReminder || isReminderDisabled
                                  ? "bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed"
                                  : "bg-white border-gray-200 text-[#374151] hover:bg-gray-50"
                              }`}
                            >
                              {isSendingReminder ? (
                                "Sending..."
                              ) : isReminderSuccess ? (
                                "Sent"
                              ) : (
                                <>
                                  <ReviewIcon />
                                  Send Reminder
                                </>
                              )}
                            </button>
                          ) : null}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReviewStatusTab;
